import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import './Templates.css';
import { motion } from "motion/react";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/free-solid-svg-icons';


// Helper to pick a random image from an array
function getRandomImage(images) {
    return images[Math.floor(Math.random() * images.length)];
}

const templateTypePreviews = {
    'agent-templates': [
        require('../../assets/imgs/previews/agent/base.jpg'),
        require('../../assets/imgs/previews/agent/skyline.jpg'),
        require('../../assets/imgs/previews/agent/futuristic.jpg'),
    ],
    'brokerage-templates': [
        require('../../assets/imgs/previews/brokerage/base.jpg'),
        require('../../assets/imgs/previews/brokerage/luxury.jpg'),
        require('../../assets/imgs/previews/brokerage/metropolitan.jpg'),
        require('../../assets/imgs/previews/brokerage/vibrance.jpg'),
    ],
    'landing-pages': [
        require('../../assets/imgs/previews/landing/base.jpg'),
        require('../../assets/imgs/previews/landing/condo.jpg'),
        require('../../assets/imgs/previews/landing/townhouse.jpg'),
        require('../../assets/imgs/previews/landing/multiplex.jpg'),
    ],
    'newsletters': [
        require('../../assets/imgs/previews/newsletters/monthlyfeatured.jpg'),
        require('../../assets/imgs/previews/newsletters/interiordesign.jpg'),
        require('../../assets/imgs/previews/newsletters/buyersguide.jpg'),
        require('../../assets/imgs/previews/newsletters/springupdate.jpg'),
    ],
};

const templateTypeMobilePreviews = {
    'agent-templates': [
        require('../../assets/imgs/previews/mobile/agent/base.jpg'),
        require('../../assets/imgs/previews/mobile/agent/luxury.jpg'),
        require('../../assets/imgs/previews/mobile/agent/metropolitan.jpg'),
        require('../../assets/imgs/previews/mobile/agent/vibrance.jpg'),
    ],
    'brokerage-templates': [
        require('../../assets/imgs/previews/mobile/brokerage/base.jpg'),
        require('../../assets/imgs/previews/mobile/brokerage/skyline.jpg'),
        require('../../assets/imgs/previews/mobile/brokerage/futuristic.jpg'),
    ],
    'landing-pages': [
        require('../../assets/imgs/previews/mobile/landing/base.jpg'),
        require('../../assets/imgs/previews/mobile/landing/condo.jpg'),
        require('../../assets/imgs/previews/mobile/landing/townhouse.jpg'),
        require('../../assets/imgs/previews/mobile/landing/multiplex.jpg'),
    ],
    'newsletters': [
        require('../../assets/imgs/previews/mobile/newsletters/monthlyfeatured.jpg'),
        require('../../assets/imgs/previews/mobile/newsletters/interiordesign.jpg'),
        require('../../assets/imgs/previews/mobile/newsletters/buyersguide.jpg'),
        require('../../assets/imgs/previews/mobile/newsletters/springupdate.jpg'),
    ],
};

const phoneFrame = require('../../assets/imgs/mockups/phone-mockup-transparent.png');

const templateTypes = [
    {
        id: 'agent-templates',
        title: 'Agent Templates',
        description: 'Stand out with a modern, high-performing website designed to showcase your listings, build credibility, and capture qualified leads.',
        cta: 'Browse Agent Templates',
        // keyPoints: ["Showcase individual listings with beautiful, modern designs.",
        //     "Build personal branding and credibility online.",
        //     "Capture leads directly from property pages.",
        //     "Mobile-friendly layouts for clients on the go."]
    },
    {
        id: 'brokerage-templates',
        title: 'Brokerage Templates',
        description: 'Build a powerful online presence for your entire team with templates designed for scalability, branding, and seamless user experience.',
        cta: 'Explore Brokerage Templates',
    },
    {
        id: 'landing-pages',
        title: 'Landing Pages',
        description: 'Launch focused, high-converting pages for listings, campaigns, and open houses designed to turn visitors into leads.',
        cta: 'View Landing Pages',
    },
    {
        id: 'newsletters',
        title: 'Newsletters',
        description: 'Stay connected with beautifully designed email templates that keep your audience engaged and informed.',
        cta: 'See Newsletter Designs',
    },
];

const Templates = () => {
    // Pick a random image for each template type on each render
    const cards = templateTypes.map(type => ({
        ...type,
        img: getRandomImage(templateTypePreviews[type.id]),
        mobileImg: getRandomImage(templateTypeMobilePreviews[type.id])
    }));

    const container = {
        hidden: { opacity: 0 },
        show: {
            opacity: 1,
            transition: {
                staggerChildren: 1,
            }
        }
    }

    const card = {
        hidden: {},
        show: {}
    }

    const itemLeft = {
        hidden: { opacity: 0, transform: "translatex(-20px)" },
        show: { opacity: 1, transform: "translatex(0px)" }
    }

    const itemRight = {
        hidden: { opacity: 0, transform: "translatex(20px)" },
        show: { opacity: 1, transform: "translatex(0px)" }
    }

    return (
        <>
            <Helmet>
                <title>Website Templates | AptoWebsites</title>
                <meta name="description" content="Browse modern, mobile-friendly real estate website templates for agents, brokerages, and landing pages. Preview and choose your design." />
                <meta name="keywords" content="real estate templates, agent website templates, brokerage website templates, landing page templates, AptoWebsites" />
                <meta property="og:title" content="Website Templates | AptoWebsites" />
                <meta property="og:description" content="Browse modern, mobile-friendly real estate website templates for agents, brokerages, and landing pages." />
                <meta property="og:type" content="website" />
                <meta property="og:url" content="https://aptowebsites.com/templates" />
                <meta property="og:image" content="https://aptowebsites.com/og-image.jpg" />
                <link rel="canonical" href="https://aptowebsites.com/templates" />
            </Helmet>
            <div className="templatebackground">
                <motion.div className="templates-main-header"
                    initial={{ transform: "translateY(-80px)" }}
                    animate={{ transform: "translateY(0px)" }}
                    transition={{ type: "spring" }}>
                    <h1 className="templates-title">Templates Built to Grow Your Real Estate Business</h1>
                    <p className="templates-subtitle">
                        Beautiful, high-performance designs tailored for agents, teams, and brokerages.
                    </p>
                    <p className="templates-subtext">
                        Launch faster with professionally crafted layouts, seamless mobile experiences,
                        and built-in tools designed to help you capture leads and scale your brand online.
                    </p>
                </motion.div>

                <motion.div className="main-template-type-list"
                    variants={container}
                    initial="hidden"
                    animate="show">
                    {cards.map((type, id) => (
                        <motion.div key={type.id} className={`template-type-card ${id % 2 === 0 ? "reverse" : ""}`}
                            variants={card}>
                            <motion.div variants={itemLeft} className="template-type-img-wrapper">
                                <Link to={`/${type.id}`}
                                >
                                    <img src={type.img} alt={type.title} className="template-type-img main-img" />
                                    <div className="phone-mockup-wrapper overlap">
                                        <img
                                            src={phoneFrame}
                                            alt="Phone Mockup"
                                            className="phone-mockup-img"
                                        />
                                        <img
                                            src={type.mobileImg}
                                            alt={`${type.title} mobile`}
                                            className="main-template-img phone-inside-img"
                                        />
                                    </div>
                                </Link>
                            </motion.div>
                            <motion.div className="template-type-info"
                                variants={itemRight}>
                                <h2>{type.title}</h2>
                                <p>{type.description}</p>
                                {type.keyPoints && (
                                    <div className="template-keypoints-wrapper">
                                        {type.keyPoints.map((keypoint, index) => (
                                            <div key={index} className="template-keypoint">
                                                <FontAwesomeIcon icon={faCheck} className="check-icon" />
                                                <p>{keypoint}</p>
                                            </div>
                                        ))}
                                    </div>
                                )}
                                <Link to={`/${type.id}`} className="template-type-link">{type.cta} &rarr;</Link>

                            </motion.div>
                        </motion.div>
                    ))}
                </motion.div>
            </div>
        </>
    );
};

export default Templates;
